import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { CalendarClock, DollarSign, PartyPopper, Mail, Users2, TrendingUp, Clock } from "lucide-react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { KpiSkeleton, BlockSkeleton } from "@/components/Loading";
import { ChartCard, ChartTooltip, CHART_COLORS, PIE_COLORS } from "@/components/charts";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { fmtCurrency, fmtNumber, fmtDate, fmtDateTime } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const axisStyle = { fontSize: 12, fill: "rgba(48,56,60,0.62)" };
const gridStroke = "rgba(82,110,126,0.18)";

function shortDate(d) {
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Overview() {
  const { isCorporate } = useAuth();
  const { queryParams, currentBrandName } = useFilters();
  const [summary, setSummary] = useState(null);
  const [byBrand, setByBrand] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const calls = [
      api.get("/metrics/summary", { params: queryParams }),
      api.get("/reservations", { params: { ...queryParams, status: "booked" } }),
    ];
    if (isCorporate) calls.push(api.get("/metrics/by-brand", { params: queryParams }));
    Promise.all(calls)
      .then((res) => {
        if (!active) return;
        setSummary(res[0].data);
        const now = new Date().toISOString();
        const up = (res[1].data || [])
          .filter((r) => (r.reservation_date || "") >= now)
          .sort((a, b) => (a.reservation_date > b.reservation_date ? 1 : -1))
          .slice(0, 6);
        setUpcoming(up);
        if (isCorporate && res[2]) setByBrand(res[2].data);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [queryParams, isCorporate]);

  const k = summary?.kpis;
  const maxRevenue = Math.max(1, ...byBrand.map((b) => b.revenue_total));

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCorporate ? "Corporate Overview" : "Dashboard"}
        subtitle={
          isCorporate
            ? "Group-wide performance across all Pack Brothers venues."
            : `Performance for ${currentBrandName}.`
        }
      />

      {loading || !k ? (
        <>
          <KpiSkeleton count={4} />
          <div className="grid gap-6 lg:grid-cols-2">
            <BlockSkeleton />
            <BlockSkeleton />
          </div>
        </>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiCard
              testId="kpi-reservations"
              label="Reservations"
              value={fmtNumber(k.reservations)}
              sub={`${fmtNumber(k.total_covers)} covers \u00b7 ${fmtNumber(k.upcoming_reservations)} upcoming`}
              icon={CalendarClock}
              accent="navy"
              delay={0}
            />
            <KpiCard
              testId="kpi-revenue"
              label="Revenue"
              value={fmtCurrency(k.revenue_total)}
              sub="Manual entries (POS coming soon)"
              icon={DollarSign}
              accent="blue"
              delay={60}
            />
            <KpiCard
              testId="kpi-inquiries"
              label="Event Inquiries"
              value={fmtNumber(k.inquiries)}
              sub={`${fmtNumber(k.new_inquiries)} new / unactioned`}
              icon={PartyPopper}
              accent="sand"
              delay={120}
            />
            <KpiCard
              testId="kpi-signups"
              label="Newsletter Signups"
              value={fmtNumber(k.signups)}
              sub="In selected period"
              icon={Mail}
              accent="muted"
              delay={180}
            />
          </div>

          {/* Trends */}
          <div className="grid gap-6 lg:grid-cols-3">
            <ChartCard title="Reservations & signups" testId="chart-reservations-trend" className="lg:col-span-2">
              {summary.trend.length === 0 ? (
                <EmptyState title="No activity in range" message="Try a wider date range." icon={TrendingUp} />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={summary.trend} margin={{ left: -12, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="gRes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.reservations} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={CHART_COLORS.reservations} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gSig" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.signups} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={CHART_COLORS.signups} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={axisStyle} axisLine={false} tickLine={false} minTickGap={28} />
                    <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} />
                    <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtNumber(v)} />} labelFormatter={shortDate} />
                    <Area type="monotone" dataKey="reservations" name="Reservations" stroke={CHART_COLORS.reservations} strokeWidth={2} fill="url(#gRes)" />
                    <Area type="monotone" dataKey="signups" name="Signups" stroke={CHART_COLORS.signups} strokeWidth={2} fill="url(#gSig)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Party sizes" testId="chart-party-sizes">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={summary.party_sizes} margin={{ left: -18, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="name" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtNumber(v)} />} cursor={{ fill: "rgba(183,165,142,0.12)" }} />
                  <Bar dataKey="value" name="Reservations" radius={[6, 6, 0, 0]} fill={CHART_COLORS.reservations} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Revenue trend + inquiry breakdown */}
          <div className="grid gap-6 lg:grid-cols-3">
            <ChartCard title="Revenue trend" testId="chart-revenue-trend" className="lg:col-span-2">
              {summary.trend.every((t) => !t.revenue) ? (
                <EmptyState title="No revenue in range" message="Add revenue entries to see the trend." icon={DollarSign} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={summary.trend} margin={{ left: 4, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.revenue} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={CHART_COLORS.revenue} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={shortDate} tick={axisStyle} axisLine={false} tickLine={false} minTickGap={28} />
                    <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={axisStyle} axisLine={false} tickLine={false} width={48} />
                    <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtCurrency(v)} />} labelFormatter={shortDate} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke={CHART_COLORS.revenue} strokeWidth={2} fill="url(#gRev)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Inquiry types" testId="chart-inquiry-types">
              {summary.inquiry_types.length === 0 ? (
                <EmptyState title="No inquiries" icon={PartyPopper} />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={summary.inquiry_types} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={52} outerRadius={82} paddingAngle={2}>
                      {summary.inquiry_types.map((e, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtNumber(v)} />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Per-brand comparison (corporate) */}
          {isCorporate && (
            <Card data-testid="chart-brand-comparison" className="rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base font-semibold text-[#18344A]">Per-brand comparison</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-[var(--pb-border)] text-left text-xs font-semibold text-[rgba(48,56,60,0.72)]">
                      <th className="py-2.5 pr-4">Brand</th>
                      <th className="py-2.5 pr-4 text-right">Reservations</th>
                      <th className="py-2.5 pr-4 text-right">Inquiries</th>
                      <th className="py-2.5 pr-4 text-right">Signups</th>
                      <th className="py-2.5 pr-4 text-right">Revenue</th>
                      <th className="py-2.5 w-[160px]">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byBrand.map((b) => (
                      <tr key={b.brand_id} data-testid={`brand-row-${b.brand_id}`} className="border-b border-[var(--pb-border)]/60 transition-colors hover:bg-[rgba(183,165,142,0.1)]">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-[#18344A]">{b.brand_name}</div>
                          <div className="text-xs text-[rgba(48,56,60,0.55)]">{b.city}, {b.state}</div>
                        </td>
                        <td className="py-3 pr-4 text-right font-num">{fmtNumber(b.reservations)}</td>
                        <td className="py-3 pr-4 text-right font-num">{fmtNumber(b.inquiries)}</td>
                        <td className="py-3 pr-4 text-right font-num">{fmtNumber(b.signups)}</td>
                        <td className="py-3 pr-4 text-right font-num font-medium text-[#18344A]">{fmtCurrency(b.revenue_total)}</td>
                        <td className="py-3">
                          <div className="h-2 w-full rounded-full bg-[rgba(82,110,126,0.12)]">
                            <div className="h-2 rounded-full bg-[#B7A58E]" style={{ width: `${(b.revenue_total / maxRevenue) * 100}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Upcoming reservations */}
          <Card className="rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-display text-base font-semibold text-[#18344A]">
                <Clock className="h-4 w-4 text-[#526E7E]" /> Upcoming reservations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming.length === 0 ? (
                <EmptyState title="No upcoming reservations" message="New bookings will appear here." icon={CalendarClock} />
              ) : (
                <div className="divide-y divide-[var(--pb-border)]/60">
                  {upcoming.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-3">
                      <div>
                        <div className="font-medium text-[#30383C]">{r.guest_name}</div>
                        <div className="text-xs text-[rgba(48,56,60,0.6)]">{fmtDateTime(r.reservation_date)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-num text-sm text-[#18344A]">Party of {r.party_size}</span>
                        <StatusBadge status={r.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
